import React, { useState } from "react";
import RichTextEditor from "react-rte";
import { DropdownList } from "react-widgets";
import api from "../../../../api";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { IAddLeadActivityModel } from "../../../../core/api/models/lead";
import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";
import ContainerFitLoader from "../../../../components/loader/container-fit-loader";
import { NoteTypes, convertPascalCaseToWords } from "../../../../core";

interface IAddNoteActivityProps {
  leadId?: string;
  documentId?: string;
  onSubmitCompleted?: () => void;
  showForm?: boolean;
}

export const AddNoteActivity: React.FunctionComponent<IAddNoteActivityProps> = (
  props
) => {
  const { addLeadActivity, updating } = useLeadActivities(api.lead);
  const [value, setValue] = useState(RichTextEditor.createEmptyValue());
  const [leadId, setLeadId] = useState(props.leadId);
  const [noteType, setNoteType] = useState(NoteTypes.freeText);
  const [leadError, setLeadError] = useState("");
  const [showEditor, setShowEditor] = useState(!!props.showForm);
  const [error, setError] = useState("");
  const toolbarConfig = {
    // Optionally specify the groups to display (displayed in the order listed).
    display: ["INLINE_STYLE_BUTTONS", "BLOCK_TYPE_BUTTONS"],
    INLINE_STYLE_BUTTONS: [
      { label: "Bold", style: "BOLD", className: "custom-css-class" },
      { label: "Italic", style: "ITALIC" },
      { label: "Underline", style: "UNDERLINE" },
    ],
    BLOCK_TYPE_DROPDOWN: [
      { label: "Normal", style: "unstyled" },
      { label: "Heading Large", style: "header-one" },
      { label: "Heading Medium", style: "header-two" },
      { label: "Heading Small", style: "header-three" },
    ],
    BLOCK_TYPE_BUTTONS: [
      { label: "UL", style: "unordered-list-item" },
      { label: "OL", style: "ordered-list-item" },
    ],
  };
  const onNoteSubmit = (note: string) => {
    if (!leadId) {
      setLeadError("Select lead");
      return;
    }

    const regex = /(<([^>]+)>)/gi;
    const hasText = !!note
      .replace(regex, "")
      .replace(/\s/g, "")
      .replace(/^(&nbsp;|<br>)+/, "").length;
    if (!hasText) {
      setError("Note is required");
      return;
    }
    if (note.length > 1000) {
      setError("Total characters should not exeed 1000");
      return;
    }
    const apiModel: IAddLeadActivityModel = {
      type: "note",
      title: "Note",
      leadId: leadId || "",
      note,
      noteType,
      documentId: props.documentId || "",
    };
    const forAll = false;
    addLeadActivity(
      apiModel,
      () => {
        setShowEditor(false);
        setValue(RichTextEditor.createEmptyValue());
        props.onSubmitCompleted?.();
      },
      forAll
    );
  };

  const renderNoteType = () => {
    return (
      <div className="form-group font-weight-semibold mt-3">
        <label className="font-sm text-uppercase">Type</label>
        <DropdownList
          value={noteType}
          defaultValue={noteType}
          data={Object.keys(NoteTypes).map((key) => {
            return {
              label: convertPascalCaseToWords(key),
              value: key,
            };
          })}
          textField={"label"}
          valueField={"value"}
          placeholder={"Select Type"}
          onChange={(val) => {
            setNoteType(val.value);
          }}
        />
      </div>
    );
  };

  return (
    <div>
      {!showEditor && (
        <div
          onClick={() => {
            setShowEditor(true);
          }}
          className="border-bottom p-4 text-gray-400 font-xs"
        >
          Click here to take notes...
        </div>
      )}
      {showEditor && (
        <div>
          <div className="col-sm-12 col-12 pt-4 ">
            <RichTextEditor
              toolbarConfig={toolbarConfig}
              value={value}
              className="new-note-editor"
              onChange={(html: any) => {
                setValue(html);
              }}
            />
            {renderNoteType()}
            {updating && <ContainerFitLoader></ContainerFitLoader>}

            <ErrorMessage>{error}</ErrorMessage>
          </div>
          <div className="border-bottom border-top container-fluid py-2 text-right">
            <button
              onClick={() => {
                if (props.showForm) {
                  props.onSubmitCompleted?.();
                } else {
                  setShowEditor(false);
                }
              }}
              className="btn btn-outline-dark font-normal font-weight-semibold py-2 px-4 mr-1"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onNoteSubmit(value.toString("html"));
              }}
              className="btn btn-primary font-normal font-weight-semibold py-2"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
